import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Lock, Loader2, CheckCircle, AlertTriangle, XCircle } from "lucide-react";
import POSHeader from "./POSHeader";
import { printCaixaReportThermal } from "@/utils/printService";

export default function FecharCaixaView({ authAxios, onBack, onCaixaFechado }) {
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [whatsappNum, setWhatsappNum] = useState("");
  const [email, setEmail] = useState("");
  const [valorContado, setValorContado] = useState("");

  useEffect(() => {
    fetchResumo();
  }, []);

  const fetchResumo = async () => {
    try {
      const [resumoRes, restRes] = await Promise.allSettled([
        authAxios.get("/pos/caixa/resumo"),
        authAxios.get("/restaurant"),
      ]);
      if (resumoRes.status === "fulfilled") setResumo(resumoRes.value.data);
      else setResumo({ valor_inicial: 0, total_vendas: 0, total_dinheiro: 0, total_cartao: 0, cancelamentos: 0, metodos: {}, esperado_em_caixa: 0, vendas: [] });
      if (restRes.status === "fulfilled") {
        const r = restRes.value.data;
        if (r?.whatsapp_number) setWhatsappNum(r.whatsapp_number);
        else if (r?.phone) setWhatsappNum(r.phone);
        if (r?.report_email) setEmail(r.report_email);
        else if (r?.email) setEmail(r.email);
      }
    } catch {
      setResumo({ valor_inicial: 0, total_vendas: 0, total_dinheiro: 0, total_cartao: 0, cancelamentos: 0, metodos: {}, esperado_em_caixa: 0, vendas: [] });
    }
    setLoading(false);
  };

  const handleFecharCaixa = async () => {
    setProcessing(true);
    try {
      await authAxios.post("/pos/caixa/fechar", {
        enviar_whatsapp: whatsappNum || null,
        enviar_email: email || null,
        valor_contado: valorContado !== "" ? parseFloat(valorContado) : null,
      });
      toast.success("Caixa fechado com sucesso!");
      if (whatsappNum) toast.success(`Relatório enviado para ${whatsappNum}`);
      if (email) toast.success(`Relatório enviado para ${email}`);
      try {
        const contadoNum = valorContado !== "" ? parseFloat(valorContado) : null;
        const esperadoNum = resumo?.esperado_em_caixa ?? ((resumo?.valor_inicial || 0) + (resumo?.total_dinheiro || 0));
        const diferencaNum = (contadoNum !== null && !isNaN(contadoNum)) ? contadoNum - esperadoNum : null;
        const rows = Object.entries(resumo?.metodos || {}).length > 0
          ? Object.entries(resumo.metodos).map(([key, val]) => ({ key, registado: Number(val), recVal: null, diff: null }))
          : [
              { key: "Dinheiro", registado: resumo?.total_dinheiro || 0, recVal: null, diff: null },
              { key: "Cartão/Outros", registado: resumo?.total_cartao || 0, recVal: null, diff: null },
            ];
        if (rows.length === 1 && rows[0].key === "Dinheiro") {
          rows[0].recVal = contadoNum;
          rows[0].diff = diferencaNum;
        }
        await printCaixaReportThermal({
          date: new Date().toLocaleString("pt-PT"),
          rows,
          totalRegistado: resumo?.total_vendas || 0,
          totalRecebido: contadoNum,
          totalDiff: diferencaNum,
          valorInicial: resumo?.valor_inicial || 0,
          totalVendas: resumo?.total_vendas || 0,
          cancelamentos: resumo?.cancelamentos || 0,
        });
      } catch (printErr) {
        console.warn("[FecharCaixa] Impressão falhou (não bloqueia):", printErr?.message);
      }
      onCaixaFechado(false);
      onBack();
    } catch { toast.error("Erro ao fechar caixa"); }
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Conferência manual
  const esperado = resumo?.esperado_em_caixa ?? ((resumo?.valor_inicial || 0) + (resumo?.total_dinheiro || 0));
  const contado = valorContado !== "" ? parseFloat(valorContado) : null;
  const diferenca = contado !== null && !isNaN(contado) ? contado - esperado : null;
  const diferencaIcon = diferenca === null ? null : diferenca === 0 ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : Math.abs(diferenca) < 5 ? <AlertTriangle className="w-5 h-5 text-amber-500" /> : <XCircle className="w-5 h-5 text-red-500" />;
  const diferencaColor = diferenca === null ? "" : diferenca === 0 ? "text-emerald-600" : Math.abs(diferenca) < 5 ? "text-amber-600" : "text-red-600";

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex flex-col" data-testid="pos-fechar-caixa">
      <POSHeader title="Fechar Caixa" icon={Lock} onBack={onBack} />
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 max-w-lg mx-auto">

          {/* Resumo */}
          <div className="bg-white rounded-xl p-4 shadow">
            <h3 className="font-bold text-gray-700 mb-3">Resumo do Dia</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Valor inicial:</span>
                <span className="font-bold">€{(resumo?.valor_inicial || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total vendas:</span>
                <span className="font-bold text-emerald-600">€{(resumo?.total_vendas || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-red-600">
                <span>Cancelamentos:</span>
                <span className="font-bold">€{(resumo?.cancelamentos || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Por método de pagamento */}
          <div className="bg-white rounded-xl p-4 shadow">
            <h3 className="font-bold text-gray-700 mb-3">Por Método de Pagamento</h3>
            <div className="space-y-2 text-sm">
              {Object.entries(resumo?.metodos || {}).length > 0 ? (
                Object.entries(resumo.metodos).map(([metodo, valor]) => (
                  <div key={metodo} className="flex justify-between">
                    <span className="text-gray-500">{metodo}:</span>
                    <span className="font-bold">€{Number(valor).toFixed(2)}</span>
                  </div>
                ))
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dinheiro:</span>
                    <span className="font-bold">€{(resumo?.total_dinheiro || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Cartão/Outros:</span>
                    <span className="font-bold">€{(resumo?.total_cartao || 0).toFixed(2)}</span>
                  </div>
                </>
              )}
              <div className="border-t pt-2 mt-1 flex justify-between font-bold">
                <span>💰 Esperado em caixa:</span>
                <span className="text-emerald-600">€{esperado.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Conferência manual */}
          <div className="bg-white rounded-xl p-4 shadow">
            <h3 className="font-bold text-gray-700 mb-3">Conferência de Caixa</h3>
            <p className="text-xs text-gray-400 mb-3">Conte o dinheiro físico em caixa e insira o valor abaixo</p>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={valorContado}
              onChange={e => setValorContado(e.target.value)}
              className="text-xl font-bold text-center"
            />
            {diferenca !== null && !isNaN(diferenca) && (
              <div className={`mt-3 flex items-center justify-between p-3 rounded-lg ${diferenca === 0 ? "bg-emerald-50" : Math.abs(diferenca) < 5 ? "bg-amber-50" : "bg-red-50"}`}>
                <div className="flex items-center gap-2">
                  {diferencaIcon}
                  <span className={`font-bold ${diferencaColor}`}>
                    {diferenca === 0 ? "Caixa confere!" : diferenca > 0 ? `Sobra €${diferenca.toFixed(2)}` : `Falta €${Math.abs(diferenca).toFixed(2)}`}
                  </span>
                </div>
                <span className="text-xs text-gray-500">Esperado: €{esperado.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Vendas do dia */}
          <div className="bg-white rounded-xl p-4 shadow">
            <h3 className="font-bold text-gray-700 mb-3">Vendas do Dia ({resumo?.vendas?.length || 0})</h3>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {(resumo?.vendas || []).map((v, i) => (
                <div key={i} className="flex justify-between text-sm border-b pb-1">
                  <span className="text-gray-600">{v.hora} — {v.canal} {v.tipo}</span>
                  <span className={`font-bold ${v.cancelado ? "text-red-500 line-through" : "text-gray-700"}`}>
                    €{v.valor?.toFixed(2)}
                  </span>
                </div>
              ))}
              {(!resumo?.vendas || resumo.vendas.length === 0) && (
                <p className="text-gray-400 text-center text-sm">Nenhuma venda hoje</p>
              )}
            </div>
          </div>

          {/* Enviar relatório */}
          <div className="bg-white rounded-xl p-4 shadow">
            <h3 className="font-bold text-gray-700 mb-3">Enviar Relatório</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">WhatsApp do proprietário</label>
                <Input placeholder="+351 999 999 999" value={whatsappNum} onChange={e => setWhatsappNum(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Email do proprietário</label>
                <Input type="email" placeholder="gerente@restaurante.com" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
            </div>
          </div>

          <Button
            className="w-full h-14 bg-red-600 hover:bg-red-700 font-bold text-lg"
            onClick={() => setShowConfirm(true)}
          >
            FECHAR CAIXA
          </Button>
        </div>
      </ScrollArea>

      {/* Confirm Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Confirmar Fechamento</DialogTitle></DialogHeader>
          <div className="space-y-2 py-4 text-center">
            <p className="text-gray-500 text-sm">Total vendas do dia</p>
            <p className="font-black text-emerald-600 text-2xl">€{(resumo?.total_vendas || 0).toFixed(2)}</p>
            {diferenca !== null && !isNaN(diferenca) && (
              <p className={`text-sm font-semibold ${diferencaColor}`}>
                {diferenca === 0 ? "✅ Caixa confere" : diferenca > 0 ? `⚠️ Sobra €${diferenca.toFixed(2)}` : `🚨 Falta €${Math.abs(diferenca).toFixed(2)}`}
              </p>
            )}
            {(whatsappNum || email) && (
              <p className="text-xs text-gray-400">Relatório será enviado para {[whatsappNum, email].filter(Boolean).join(" e ")}</p>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setShowConfirm(false)}>Cancelar</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleFecharCaixa} disabled={processing}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
